import React from 'react';
import PropTypes from 'prop-types';
import { useLabels } from 'wsm-common-data';
import { buildPaginationUrl, getSortedPagination } from 'wsm-srp-utilities';
import {
	getPageNumber,
	stripPersonalizationParams
} from '../../utilities/pagination';

/**
 * Static, crawlable pagination nav rendered on the AI-bot SSR path
 * (US1994526 / TA4069141, AC6). Plain <a href="…?start=<offset>"> links so a
 * rendering crawler can follow to subsequent SRP/TRP pages with no JS.
 *
 * It MIRRORS the interactive ws-inv-paging widget: same windowed page-set (via
 * the ported getSortedPagination — NOT one link per page) and the same
 * nav > ul.pagination markup/classes, so it looks like the real widget while
 * staying static. It lives inside Widget.jsx's bot-only isSeoPhase subtree, so
 * it is replaced on hydration / Phase B and never lingers beside the real
 * ws-inv-paging widget for a misidentified human (ADR-0014 Decision step 6).
 *
 * Renders purely from props seeded into the `seoVehicleData` slice server-side
 * (`pageInfo`, `baseUrl`, `parameterMap`) — with no live window/location reads
 * and no `useRequestData()`. ws-scripts serializes only a subset of requestData
 * to the client (uri/parameterMap are dropped), so reading them from the hook
 * would render path-less hrefs on the client and mismatch the SSR markup;
 * sourcing them from the fully-serialized slice keeps server + client identical
 * (clean hydrate, AC5). useLabels() resolves synchronously during renderToString.
 *
 * Hrefs come from wsm-srp-utilities' shared `buildPaginationUrl` (the canonical
 * SRP `start=<offset>` builder). Geo / personalization params are stripped first
 * (stripPersonalizationParams, per ADR-0014 D1) so the crawlable URLs carry only
 * content-defining params — facets + sort + the start offset are kept; geo is
 * dropped (the bot content is geo-neutral). ws-inv-data's head rel=prev/next may
 * still carry geo (the complementary hint); the body anchors are the clean signal.
 *
 * Labels reuse wsm-paging-display's pagination keys (PAGINATION,
 * GO_TO_PREVIOUS_PAGE, GO_TO_NEXT_PAGE, GO_TO_PAGE_X — the last carries a
 * {pageNumber} token) so the crawlable nav shares Label Services entries with the
 * interactive paging UI rather than minting new ones. The current page relies on
 * aria-current="page" (no extra sr-only text needed).
 */
const SeoPagination = ({
	pageInfo,
	baseUrl,
	parameterMap: rawParameterMap
}) => {
	const labels = useLabels();

	// Crawlable links carry only content-defining params — strip geo /
	// personalization (ADR-0014 D1). `parameterMap` is the cleaned set below.
	const parameterMap = stripPersonalizationParams(rawParameterMap);

	const { pageStart = 0, pageSize = 0, totalCount = 0 } = pageInfo || {};

	// Nothing to paginate: no page size, or a single page of results.
	const totalPages = pageSize > 0 ? Math.ceil(totalCount / pageSize) : 0;
	if (totalPages <= 1) {
		return null;
	}

	// ws-inv-paging works in 0-indexed page indices; getPageNumber is 1-indexed.
	const currentPage = getPageNumber(pageStart, pageSize); // 1-indexed
	const currentPageIndex = currentPage - 1; // 0-indexed
	const lastPageIndex = totalPages - 1;

	// Base path the links resolve against — query stripped. Seeded into the
	// seoVehicleData slice from requestData.uri server-side (see the slice doc).
	const pageUrl = (baseUrl || '').split('?')[0];

	const sorted = getSortedPagination(currentPageIndex, {
		totalCount,
		pageSize
	});

	const goToPageTemplate = labels.get('GO_TO_PAGE_X');

	const pageItems = [];
	sorted.forEach((pageIndex, i) => {
		const pageNumber = pageIndex + 1;
		const isCurrent = pageIndex === currentPageIndex;

		pageItems.push(
			<li key={pageIndex} className={isCurrent ? 'active' : null}>
				{isCurrent ? (
					<span aria-current="page">{pageNumber}</span>
				) : (
					<a
						href={buildPaginationUrl(
							pageNumber,
							pageSize,
							parameterMap,
							pageUrl
						)}
						aria-label={goToPageTemplate.replace(
							'{pageNumber}',
							pageNumber
						)}
					>
						{pageNumber}
					</a>
				)}
			</li>
		);

		// Insert an ellipsis wherever the windowed set skips page numbers.
		if (sorted[i + 1] && pageIndex + 1 !== sorted[i + 1]) {
			pageItems.push(
				<li
					key={`ellipsis-${pageIndex}`}
					className="pagination-ellipsis"
				>
					<span>&hellip;</span>
				</li>
			);
		}
	});

	return (
		<div className="d-flex justify-content-end">
			<nav aria-label={labels.get('PAGINATION')}>
				<ul className="pagination">
					{currentPageIndex > 0 && (
						<li className="pagination-previous">
							<a
								href={buildPaginationUrl(
									currentPage - 1,
									pageSize,
									parameterMap,
									pageUrl
								)}
								aria-label={labels.get('GO_TO_PREVIOUS_PAGE')}
							>
								<i
									className="ddc-icon ddc-icon-arrow1-left"
									aria-hidden="true"
								/>
							</a>
						</li>
					)}
					{pageItems}
					{currentPageIndex < lastPageIndex && (
						<li className="pagination-next">
							<a
								href={buildPaginationUrl(
									currentPage + 1,
									pageSize,
									parameterMap,
									pageUrl
								)}
								aria-label={labels.get('GO_TO_NEXT_PAGE')}
							>
								<i
									className="ddc-icon ddc-icon-arrow1-right"
									aria-hidden="true"
								/>
							</a>
						</li>
					)}
				</ul>
			</nav>
		</div>
	);
};

SeoPagination.propTypes = {
	pageInfo: PropTypes.shape({
		pageStart: PropTypes.number,
		pageSize: PropTypes.number,
		totalCount: PropTypes.number
	}),
	// Seeded into the seoVehicleData slice from requestData server-side.
	baseUrl: PropTypes.string,
	parameterMap: PropTypes.objectOf(
		PropTypes.oneOfType([
			PropTypes.string,
			PropTypes.arrayOf(PropTypes.string)
		])
	)
};

SeoPagination.defaultProps = {
	pageInfo: {},
	baseUrl: '',
	parameterMap: {}
};

export default SeoPagination;
